import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface CreateCustomerForm {
  companyName: string;
  contactName: string;
  email: string;
  phone?: string;
  addressLine1?: string;
  city?: string;
  country?: string;
}

interface CreateCustomerModalProps {
  onCustomerCreated: (customer: { id: string; companyName: string; contactName: string; email: string; phone: string | null }) => void;
  trigger?: React.ReactNode;
}

export function CreateCustomerModal({ onCustomerCreated, trigger }: CreateCustomerModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const [open, setOpen] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateCustomerForm>({
    defaultValues: {
      companyName: '',
      contactName: '',
      email: '',
      phone: '',
      addressLine1: '',
      city: '',
      country: '',
    },
  });

  const utils = trpc.useUtils();

  const createMutation = trpc.tradingCustomer.create.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('customers.createdSuccess'));
      utils.tradingCustomer.list.invalidate();
      onCustomerCreated({
        id: data.id,
        companyName: data.companyName,
        contactName: data.contactName,
        email: data.email,
        phone: data.phone,
      });
      setOpen(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const onSubmit = (data: CreateCustomerForm) => {
    createMutation.mutate({
      companyName: data.companyName,
      contactName: data.contactName,
      email: data.email,
      phone: data.phone || undefined,
      addressLine1: data.addressLine1 || undefined,
      city: data.city || undefined,
      country: data.country || undefined,
    });
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      reset();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        {trigger || (
          <Button type="button" variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1" />
            {t('customers.createNew')}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {t('customers.createNew')}
          </DialogTitle>
          <DialogDescription>
            {t('customers.createNewDescription')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="companyName">
                {t('customers.companyName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="companyName"
                {...register('companyName', { required: t('customers.companyNameRequired') })}
                placeholder={t('customers.companyNamePlaceholder')}
              />
              {errors.companyName && (
                <p className="text-xs text-red-500">{errors.companyName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactName">
                {t('customers.contactName')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="contactName"
                {...register('contactName', { required: t('customers.contactNameRequired') })}
                placeholder={t('customers.contactNamePlaceholder')}
              />
              {errors.contactName && (
                <p className="text-xs text-red-500">{errors.contactName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                {t('customers.email')} <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('customers.emailRequired'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t('customers.emailInvalid'),
                  },
                })}
                placeholder={t('customers.emailPlaceholder')}
              />
              {errors.email && (
                <p className="text-xs text-red-500">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('customers.phone')}</Label>
              <Input
                id="phone"
                {...register('phone')}
                placeholder={t('customers.phonePlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="addressLine1">{t('customers.address')}</Label>
              <Input
                id="addressLine1"
                {...register('addressLine1')}
                placeholder={t('customers.addressPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">{t('customers.city')}</Label>
              <Input
                id="city"
                {...register('city')}
                placeholder={t('customers.cityPlaceholder')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="country">{t('customers.country')}</Label>
              <Input
                id="country"
                {...register('country')}
                placeholder={t('customers.countryPlaceholder')}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={createMutation.isPending}>
              {createMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t('customers.create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
